import { COMPANY, LEGAL_EFFECTIVE_DATE } from './company'
import type { LegalPageContent } from './content-pages'

const CONTACT_BLOCKS = [
  {
    kind: 'paragraph' as const,
    text: `${COMPANY.legalName}`,
  },
  {
    kind: 'list' as const,
    items: [
      `Address: ${COMPANY.address}`,
      `Email: ${COMPANY.email}`,
      `Customer support: ${COMPANY.phoneDisplay}`,
      `Website: ${COMPANY.websiteLabel}`,
    ],
  },
]

export const PRIVACY_POLICY: LegalPageContent = {
  title: 'Privacy Policy',
  lead: `At ${COMPANY.legalName} ("Maaliondoor", "we", "our", or "us"), we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, disclose, and safeguard your information when you visit ${COMPANY.websiteLabel} or use our products and services.`,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      title: '1. Information we collect',
      blocks: [
        { kind: 'paragraph', text: 'We may collect the following information:' },
        { kind: 'paragraph', text: 'Personal information' },
        {
          kind: 'list',
          items: [
            'Full name',
            'Mobile number',
            'Email address',
            'Billing & delivery address',
            'Company name (for business customers)',
            'GST number (if applicable)',
          ],
        },
        { kind: 'paragraph', text: 'Order information' },
        {
          kind: 'list',
          items: [
            'Order details',
            'Product preferences',
            'Delivery instructions',
            'Payment information (processed securely through payment gateways)',
          ],
        },
        { kind: 'paragraph', text: 'Technical information' },
        {
          kind: 'list',
          items: [
            'IP address',
            'Browser type',
            'Device information',
            'Operating system',
            'Website usage data',
            'Cookies and analytics data',
          ],
        },
      ],
    },
    {
      title: '2. How we use your information',
      blocks: [
        { kind: 'paragraph', text: 'We use your information to:' },
        {
          kind: 'list',
          items: [
            'Process and deliver your orders.',
            'Manage payments and invoices.',
            'Provide customer support.',
            'Send order confirmations and delivery updates.',
            'Improve our website and services.',
            'Respond to inquiries and feedback.',
            'Send promotional offers and marketing communications (only with your consent).',
            'Prevent fraud and ensure website security.',
            'Comply with legal and regulatory requirements.',
          ],
        },
      ],
    },
    {
      title: '3. Payment security',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor does not store your debit card, credit card, UPI PIN, CVV, or banking credentials.',
        },
        {
          kind: 'paragraph',
          text: 'All online payments are processed through secure third-party payment gateways that use industry-standard encryption and security protocols.',
        },
      ],
    },
    {
      title: '4. Cookies',
      blocks: [
        { kind: 'paragraph', text: 'Our website uses cookies to:' },
        {
          kind: 'list',
          items: [
            'Improve website functionality.',
            'Remember your preferences.',
            'Analyze website traffic.',
            'Enhance user experience.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'You may disable cookies through your browser settings; however, some website features may not function properly.',
        },
      ],
    },
    {
      title: '5. Sharing of information',
      blocks: [
        { kind: 'paragraph', text: 'We may share your information only with:' },
        {
          kind: 'list',
          items: [
            'Delivery partners',
            'Payment gateway providers',
            'Technology service providers',
            'Government or legal authorities, when required by law',
          ],
        },
        {
          kind: 'paragraph',
          text: 'We do not sell, rent, or trade your personal information to third parties for marketing purposes.',
        },
      ],
    },
    {
      title: '6. Data security',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We implement appropriate technical and organizational security measures to protect your information from unauthorized access, misuse, alteration, or disclosure.',
        },
        {
          kind: 'paragraph',
          text: 'However, no internet transmission or electronic storage method is completely secure, and we cannot guarantee absolute security.',
        },
      ],
    },
    {
      title: '7. Marketing communications',
      blocks: [
        { kind: 'paragraph', text: 'With your permission, we may send you:' },
        {
          kind: 'list',
          items: [
            'Promotional offers',
            'New product launches',
            'Festival campaigns',
            'Corporate gifting updates',
            'Special discounts',
          ],
        },
        {
          kind: 'paragraph',
          text: 'You may unsubscribe from marketing communications at any time by clicking the unsubscribe link or contacting us.',
        },
      ],
    },
    {
      title: '8. Third-party links',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Our website may contain links to third-party websites. Maaliondoor is not responsible for the privacy practices, content, or policies of those websites. We encourage users to review their respective privacy policies before sharing personal information.',
        },
      ],
    },
    {
      title: '9. Your rights',
      blocks: [
        { kind: 'paragraph', text: 'You have the right to:' },
        {
          kind: 'list',
          items: [
            'Access your personal information.',
            'Request correction of inaccurate information.',
            'Request deletion of your personal data, subject to legal obligations.',
            'Withdraw consent for marketing communications.',
            'Raise concerns regarding the use of your information.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'To exercise these rights, please contact us using the details below.',
        },
      ],
    },
    {
      title: "10. Children's privacy",
      blocks: [
        {
          kind: 'paragraph',
          text: 'Our website and services are not intended for children under the age of 18. We do not knowingly collect personal information from minors without parental or legal guardian consent.',
        },
      ],
    },
    {
      title: '11. Data retention',
      blocks: [
        { kind: 'paragraph', text: 'We retain your information only for as long as necessary to:' },
        {
          kind: 'list',
          items: [
            'Fulfill your orders.',
            'Provide customer support.',
            'Meet legal, tax, and accounting obligations.',
            'Resolve disputes and enforce our agreements.',
          ],
        },
      ],
    },
    {
      title: '12. Changes to this Privacy Policy',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor reserves the right to update or modify this Privacy Policy at any time. Any changes will be posted on this page with the updated effective date. Continued use of the website after changes are published constitutes your acceptance of the revised Privacy Policy.',
        },
      ],
    },
    {
      title: '13. Contact us',
      blocks: [
        {
          kind: 'paragraph',
          text: 'If you have any questions or concerns regarding this Privacy Policy or how we handle your personal information, please contact us:',
        },
        ...CONTACT_BLOCKS,
      ],
    },
  ],
  closing:
    'Your privacy matters. At Maaliondoor, we are committed to protecting your personal information and maintaining the highest standards of privacy, transparency, and security. We value your trust and continuously work to ensure a safe and reliable shopping experience for every customer.',
}

export const TERMS_AND_CONDITIONS: LegalPageContent = {
  title: 'Terms & Conditions',
  lead: `Welcome to ${COMPANY.legalName} ("Maaliondoor", "we", "our", or "us"). These Terms & Conditions govern your access to and use of our website ${COMPANY.websiteLabel} and all products and services offered by us. By using our website or placing an order, you agree to be bound by these Terms & Conditions.`,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      title: '1. Company information',
      blocks: [
        {
          kind: 'list',
          items: [
            `Company name: ${COMPANY.legalName}`,
            `Registered office: ${COMPANY.address}`,
            `GSTIN: ${COMPANY.gstin}`,
            `Website: ${COMPANY.websiteLabel}`,
            `Email: ${COMPANY.email}`,
            `Customer support: ${COMPANY.phoneDisplay}`,
          ],
        },
      ],
    },
    {
      title: '2. Acceptance of terms',
      blocks: [
        {
          kind: 'paragraph',
          text: 'By accessing this website, creating an account, or placing an order, you confirm that you have read, understood, and agreed to these Terms & Conditions.',
        },
      ],
    },
    {
      title: '3. Eligibility',
      blocks: [
        {
          kind: 'list',
          items: [
            'You must be at least 18 years of age or use the website under the supervision of a parent or legal guardian.',
            'You agree to provide accurate, complete, and up-to-date information while placing an order.',
          ],
        },
      ],
    },
    {
      title: '4. Products & services',
      blocks: [
        { kind: 'paragraph', text: 'Maaliondoor offers:' },
        {
          kind: 'list',
          items: [
            'Fresh flower bouquets',
            'Indoor & outdoor plants',
            'Corporate gifting',
            'Gift hampers',
            'Employee welcome kits',
            'Plant rental & maintenance',
            'Landscaping services',
            'Event & festival decorations',
            'Floral decorations',
            'Customized gifting solutions',
          ],
        },
        { kind: 'paragraph', text: 'All products and services are subject to availability.' },
      ],
    },
    {
      title: '5. Orders & acceptance',
      blocks: [
        {
          kind: 'list',
          items: [
            'Orders are confirmed only after successful payment or confirmation by Maaliondoor.',
            'We reserve the right to refuse or cancel any order due to product unavailability, pricing errors, suspected fraud, or unforeseen circumstances.',
          ],
        },
      ],
    },
    {
      title: '6. Pricing & payment',
      blocks: [
        {
          kind: 'list',
          items: [
            'All prices are displayed in Indian Rupees (INR).',
            'Prices may change without prior notice.',
            'GST and other applicable taxes will be charged as per Indian law.',
            'Payment must be made through the payment methods available on the website.',
          ],
        },
      ],
    },
    {
      title: '7. Order cancellation',
      blocks: [
        {
          kind: 'list',
          items: [
            'Orders may be cancelled within 5 minutes of confirmation, subject to processing status.',
            'Cancellations made after 5 minutes of order confirmation may attract a 50% cancellation charge.',
            'Orders that have been prepared, customized, dispatched, or delivered cannot be cancelled.',
          ],
        },
      ],
    },
    {
      title: '8. Delivery terms',
      blocks: [
        {
          kind: 'list',
          items: [
            'Delivery timelines are estimates and may vary depending on location, weather, traffic, festivals, or other unforeseen circumstances.',
            'Customers are responsible for providing the correct delivery address and recipient contact details.',
            'Maaliondoor is not responsible for delays or failed deliveries due to incorrect information provided by the customer.',
          ],
        },
      ],
    },
    {
      title: '9. Product substitution',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Flowers, plants, wrapping materials, gift items, or decorative accessories may vary based on seasonal availability. In such cases, Maaliondoor reserves the right to substitute products with items of equal or higher value while maintaining the overall quality and design.',
        },
      ],
    },
    {
      title: '10. Corporate & event decoration',
      blocks: [
        {
          kind: 'list',
          items: [
            'A minimum 50% advance payment is required before commencement of work.',
            'Decoration work will begin only after order confirmation and receipt of the advance payment.',
            'Any additional work or changes requested after approval may incur extra charges.',
            'Advance payments are non-refundable once materials have been procured or work has commenced.',
          ],
        },
      ],
    },
    {
      title: '11. Refund & returns',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Refunds and returns are governed by our Refund & Returns Policy, available on our website.',
        },
      ],
    },
    {
      title: '12. Intellectual property',
      blocks: [
        {
          kind: 'paragraph',
          text: `All website content, including text, graphics, logos, images, videos, icons, designs, and trademarks, is the exclusive property of ${COMPANY.legalName} and is protected under applicable intellectual property laws. Unauthorized use, copying, or reproduction is prohibited.`,
        },
      ],
    },
    {
      title: '13. User conduct',
      blocks: [
        { kind: 'paragraph', text: 'You agree not to:' },
        {
          kind: 'list',
          items: [
            'Use the website for any unlawful purpose.',
            'Provide false or misleading information.',
            'Attempt to gain unauthorized access to the website or its systems.',
            'Upload harmful software, viruses, or malicious code.',
            'Copy or misuse website content without prior written permission.',
          ],
        },
      ],
    },
    {
      title: '14. Limitation of liability',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our website, products, or services, including delays caused by circumstances beyond our reasonable control.',
        },
      ],
    },
    {
      title: '15. Force majeure',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor shall not be held responsible for delays or non-performance caused by events beyond its control, including but not limited to:',
        },
        {
          kind: 'list',
          items: [
            'Natural disasters',
            'Floods',
            'Fire',
            'Pandemic',
            'Government restrictions',
            'Strikes',
            'Transportation disruptions',
            'Weather conditions',
            'Internet or technical failures',
          ],
        },
      ],
    },
    {
      title: '16. Privacy',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Your use of this website is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information.',
        },
      ],
    },
    {
      title: '17. Third-party services',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Our website may integrate with third-party payment gateways, logistics providers, or external websites. Maaliondoor is not responsible for the content, services, or policies of such third-party platforms.',
        },
      ],
    },
    {
      title: '18. Governing law & jurisdiction',
      blocks: [
        {
          kind: 'paragraph',
          text: 'These Terms & Conditions shall be governed by the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts located in Noida, Uttar Pradesh.',
        },
      ],
    },
    {
      title: '19. Changes to Terms & Conditions',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor reserves the right to update or modify these Terms & Conditions at any time without prior notice. Updated versions will be published on this page, and continued use of the website constitutes acceptance of the revised terms.',
        },
      ],
    },
    {
      title: '20. Contact us',
      blocks: [
        {
          kind: 'paragraph',
          text: 'For any questions regarding these Terms & Conditions, please contact us:',
        },
        ...CONTACT_BLOCKS,
      ],
    },
  ],
  closing: `By accessing or using ${COMPANY.websiteLabel}, you acknowledge that you have read, understood, and agreed to these Terms & Conditions. We thank you for choosing Maaliondoor and look forward to serving you with premium flowers, plants, gifting solutions, and event decoration services.`,
}

export const REFUND_AND_RETURNS_POLICY: LegalPageContent = {
  title: 'Refund & Returns Policy',
  lead: `At ${COMPANY.legalName} ("Maaliondoor"), customer satisfaction is our priority. As we deal with perishable products such as flowers, plants, cakes, and customized gifts, our Refund & Returns Policy is designed to be fair while maintaining product quality and hygiene. By placing an order on ${COMPANY.websiteLabel}, you agree to the following terms.`,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      title: '1. Order cancellation policy',
      blocks: [
        { kind: 'paragraph', text: 'Before order confirmation' },
        {
          kind: 'paragraph',
          text: 'Orders can be cancelled free of charge before they are confirmed for processing.',
        },
        { kind: 'paragraph', text: 'After order confirmation' },
        {
          kind: 'list',
          items: [
            'Orders cancelled within 5 minutes of confirmation may be eligible for cancellation.',
            'Orders cancelled after 5 minutes of confirmation will incur a 50% cancellation charge, as processing and preparation begin immediately.',
            'Orders that have already been dispatched or are out for delivery cannot be cancelled.',
          ],
        },
      ],
    },
    {
      title: '2. Return policy',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Due to the perishable and customized nature of our products, returns are generally not accepted.',
        },
        { kind: 'paragraph', text: 'The following products are non-returnable:' },
        {
          kind: 'list',
          items: [
            'Fresh flower bouquets',
            'Floral arrangements',
            'Cakes',
            'Plants',
            'Chocolates',
            'Personalized gifts',
            'Customized gift hampers',
            'Customized welcome kits',
            'Event decoration materials',
            'Festival decoration services',
          ],
        },
      ],
    },
    {
      title: '3. Refund eligibility',
      blocks: [
        { kind: 'paragraph', text: 'Refunds may be approved only in the following situations:' },
        {
          kind: 'list',
          items: [
            'Wrong product delivered.',
            'Damaged product received.',
            'Product delivered in unusable condition.',
            'Order not delivered due to our operational failure.',
            'Duplicate payment made by the customer.',
            'Order cancelled by Maaliondoor due to product unavailability.',
          ],
        },
      ],
    },
    {
      title: '4. Situations where refunds will not be provided',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Refund requests will not be accepted in the following cases:',
        },
        {
          kind: 'list',
          items: [
            'Incorrect or incomplete delivery address provided by the customer.',
            'Incorrect recipient contact number.',
            'Recipient unavailable at the delivery location.',
            'Delivery refused by the recipient.',
            'Delay caused by traffic, weather, festivals, strikes, or other events beyond our control.',
            'Minor variations in flower type, color, wrapping, or packaging due to seasonal availability.',
            'Natural variation in flowers and plants.',
          ],
        },
      ],
    },
    {
      title: '5. Replacement policy',
      blocks: [
        {
          kind: 'paragraph',
          text: 'If an eligible complaint is verified, Maaliondoor may, at its discretion:',
        },
        {
          kind: 'list',
          items: [
            'Replace the product with a similar or higher-value product.',
            'Re-deliver the order.',
            'Provide a partial refund.',
            'Provide a full refund, where applicable.',
          ],
        },
      ],
    },
    {
      title: '6. Decoration services',
      blocks: [
        {
          kind: 'paragraph',
          text: 'For corporate, event, and festival decoration services:',
        },
        {
          kind: 'list',
          items: [
            'The 50% advance payment is non-refundable once materials have been procured or work has commenced.',
            'Any changes requested after approval may result in additional charges.',
            'Refunds will not be provided after the decoration work has been completed as agreed.',
          ],
        },
      ],
    },
    {
      title: '7. Plant orders',
      blocks: [
        { kind: 'paragraph', text: 'Plants are living products and may vary slightly in:' },
        {
          kind: 'list',
          items: ['Height', 'Shape', 'Leaf size', 'Color', 'Growth stage'],
        },
        {
          kind: 'paragraph',
          text: 'These natural variations are not considered defects and do not qualify for returns or refunds.',
        },
      ],
    },
    {
      title: '8. Damaged or incorrect orders',
      blocks: [
        { kind: 'paragraph', text: 'If you receive a damaged or incorrect product:' },
        {
          kind: 'list',
          items: [
            'Notify us within 2 hours of delivery.',
            'Share clear photographs or videos of the product.',
            'Include your Order ID and contact details.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Claims received after this period may not be eligible for replacement or refund.',
        },
      ],
    },
    {
      title: '9. Refund process',
      blocks: [
        { kind: 'paragraph', text: 'If a refund is approved:' },
        {
          kind: 'list',
          items: [
            'Refunds will be processed to the original payment method.',
            'Processing may take 5–7 business days, depending on your bank or payment provider.',
          ],
        },
      ],
    },
    {
      title: '10. Force majeure',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor shall not be responsible for delays or failure in delivery caused by circumstances beyond our reasonable control, including:',
        },
        {
          kind: 'list',
          items: [
            'Natural disasters',
            'Heavy rainfall or floods',
            'Government restrictions',
            'Public holidays',
            'Strikes',
            'Transportation disruptions',
            'Pandemic-related restrictions',
          ],
        },
      ],
    },
    {
      title: '11. Contact us',
      blocks: [
        { kind: 'paragraph', text: 'For refund or return-related queries, please contact us:' },
        ...CONTACT_BLOCKS,
      ],
    },
  ],
  closing:
    'Note: By placing an order with Maaliondoor, you acknowledge that you have read, understood, and agreed to this Refund & Returns Policy. Our goal is to provide high-quality products and exceptional customer service while ensuring a transparent and hassle-free shopping experience.',
}

export const WEBSITE_DISCLAIMER: LegalPageContent = {
  title: 'Website Disclaimer',
  lead: `Welcome to ${COMPANY.legalName} ("Maaliondoor", "we", "our", or "us"). By accessing or using ${COMPANY.websiteLabel}, you agree to the terms of this Disclaimer. If you do not agree with any part of this Disclaimer, please refrain from using our website.`,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      title: '1. General information',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The information provided on this website is for general informational and commercial purposes only. While we strive to ensure that all information is accurate, complete, and up to date, Maaliondoor makes no warranties or representations regarding the accuracy, reliability, or completeness of the content.',
        },
      ],
    },
    {
      title: '2. Product representation',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We make every effort to display our flowers, plants, gifts, and decorations as accurately as possible. However, due to the natural characteristics of flowers and plants, seasonal availability, lighting, and screen settings, the actual product may vary slightly in color, size, shape, packaging, or appearance from the images displayed on the website.',
        },
      ],
    },
    {
      title: '3. Product availability',
      blocks: [
        {
          kind: 'paragraph',
          text: 'All products and services are subject to availability. We reserve the right to substitute flowers, plants, containers, wrapping materials, or other items of equal or higher value if the original products are unavailable due to seasonal or supply constraints.',
        },
      ],
    },
    {
      title: '4. Pricing',
      blocks: [
        {
          kind: 'paragraph',
          text: 'All prices displayed on the website are subject to change without prior notice. Applicable taxes, delivery charges, and other fees may be added during checkout, where applicable.',
        },
      ],
    },
    {
      title: '5. Delivery',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Delivery timelines are estimates and may vary due to weather conditions, traffic, public holidays, festivals, force majeure events, or unforeseen operational circumstances. While we strive for timely delivery, we do not guarantee delivery at an exact time unless specifically agreed upon.',
        },
      ],
    },
    {
      title: '6. Third-party services',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Our website may contain links to third-party websites or services. Maaliondoor is not responsible for the content, privacy practices, or services provided by these third parties.',
        },
      ],
    },
    {
      title: '7. Intellectual property',
      blocks: [
        {
          kind: 'paragraph',
          text: `All content on this website, including text, graphics, logos, images, designs, videos, and trademarks, is the property of ${COMPANY.legalName} unless otherwise stated. Unauthorized use, reproduction, or distribution of any content is strictly prohibited.`,
        },
      ],
    },
    {
      title: '8. Limitation of liability',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from the use of this website, products, or services, including delays, interruptions, technical issues, or errors beyond our reasonable control.',
        },
      ],
    },
    {
      title: '9. User responsibility',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Users are responsible for providing accurate recipient details, delivery addresses, contact information, and order instructions. Maaliondoor shall not be responsible for failed or delayed deliveries due to incorrect or incomplete information provided by the customer.',
        },
      ],
    },
    {
      title: '10. Changes to this Disclaimer',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Maaliondoor reserves the right to modify or update this Disclaimer at any time without prior notice. Changes will become effective immediately upon publication on the website.',
        },
      ],
    },
    {
      title: '11. Contact us',
      blocks: [
        {
          kind: 'paragraph',
          text: 'If you have any questions regarding this Disclaimer, please contact us:',
        },
        ...CONTACT_BLOCKS,
      ],
    },
  ],
  closing: `${COMPANY.legalName} is committed to providing quality products, transparent business practices, and exceptional customer service. We appreciate your trust and support.`,
}
